import { Draggable, Droppable } from '@hello-pangea/dnd';
import React, { FC, useMemo, useState } from 'react';
import { MovementData, useGetAllMovements } from 'src/api/queries/useGetAllMovements';
import { ReactComponent as CloseIcon } from 'src/assets/images/trainer/clients/close.svg';
import { useDebounce } from 'src/hooks/useDebounce';
import { isFemaleProfile } from 'src/utils/genderUtils';
import { exerciseImagesMap } from './ExerciseImages';

import { programGeneratorSVG } from '../ProgramGeneratorAI/assets/ProgramGenerator';

interface DraggableExerciseItemProps {
  ex: MovementData;
  tags: { label: string; type: string }[];
  getImagePath: (ex: MovementData) => string | null;
  getFallbackImagePath?: (ex: MovementData) => string | null;
  index: number;
}

const DraggableExerciseItem: FC<DraggableExerciseItemProps> = ({
  ex,
  tags,
  getImagePath,
  getFallbackImagePath,
  index,
}) => {
  return (
    <Draggable draggableId={`lib-${ex.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`exercise-item ${snapshot.isDragging ? 'is-dragging' : ''}`}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <div className="drag-handle">{programGeneratorSVG?.sidebar?.exercisePickerDots}</div>
          <div
            className="ex-thumb"
            style={{
              position: 'relative',
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: 8,
              overflow: 'hidden',
              backgroundColor: '#2a2d3d',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#2a2d3d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                color: '#8a8d9d',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {(ex.en_display_name || 'U').slice(0, 2).toUpperCase()}
            </div>
            {getImagePath(ex) && (
              <img
                src={getImagePath(ex)!}
                alt={ex.en_display_name}
                loading="eager"
                decoding="async"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  const fallback = getFallbackImagePath ? getFallbackImagePath(ex) : null;
                  const img = e.target as HTMLImageElement;
                  if (fallback && img.src !== fallback) {
                    img.src = fallback;
                  } else {
                    img.style.display = 'none';
                  }
                }}
              />
            )}
          </div>
          <div className="ex-info">
            <h4>{ex.en_display_name}</h4>
            {!(ex as any).isRest && (
              <div className="ex-tags">
                {tags.map((tag, i) => (
                  <span key={i} className={`tag ${tag.type}`}>
                    {tag.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

interface ExerciseLibraryProps {
  gender?: string; // 'Male' | 'Female'
  isOpen?: boolean;
  onClose?: () => void;
}

export const ExerciseLibrary: FC<ExerciseLibraryProps> = ({ gender = 'Male', isOpen, onClose }) => {
  const [activeMuscle, setActiveMuscle] = useState('All');
  const [activeType, setActiveType] = useState('All');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 300);

  const { data: movements = [], isLoading } = useGetAllMovements();

  const muscleGroups = ['All', 'Lower Body', 'Upper Body', 'Full Body', 'Core'];
  const exerciseTypes = ['All', 'Warmup', 'Main', 'Cooldown', 'Cardio'];

  const getDummyTags = (ex: MovementData) => {
    const tags: { label: string; type: string }[] = [];

    // 1. Muscle group tag (yellow) based on tags
    const tagsLower = (ex.tags || []).map((t) => t.toLowerCase().trim());
    let muscleGroup = ex.mapped_muscle_group || 'Full Body';

    if (activeMuscle !== 'All') {
      const activeLower = activeMuscle.toLowerCase();
      if (
        tagsLower.includes(activeLower) ||
        (activeLower === 'core' && tagsLower.some((t) => t.includes('core')))
      ) {
        muscleGroup = activeMuscle;
      }
    } else {
      if (tagsLower.includes('core') || tagsLower.some((t) => t.includes('core'))) {
        muscleGroup = 'Core';
      } else if (tagsLower.includes('upper body')) {
        muscleGroup = 'Upper Body';
      } else if (tagsLower.includes('lower body')) {
        muscleGroup = 'Lower Body';
      } else if (tagsLower.includes('full body')) {
        muscleGroup = 'Full Body';
      }
    }
    tags.push({ label: muscleGroup, type: 'yellow' });

    // 2. All movement types strictly from ex.move_types (green) - e.g. Warmup, Main, Cooldown
    const recognizedMoveTypes = ['warmup', 'main', 'cooldown', 'rest'];
    const seen = new Set<string>();

    (ex.move_types || []).forEach((t) => {
      if (!t) return;
      const lower = t.trim().toLowerCase();
      if (recognizedMoveTypes.includes(lower) && !seen.has(lower)) {
        seen.add(lower);
        tags.push({
          label: lower.charAt(0).toUpperCase() + lower.slice(1),
          type: 'green',
        });
      }
    });

    // 3. Cardio tag from ex.tags
    const hasCardio = (ex.tags || []).some((t) => t.toLowerCase().trim() === 'cardio');
    if (hasCardio) {
      tags.push({ label: 'Cardio', type: 'cardio' });
    }

    // If an active type filter is selected (other than 'All'), float it to the front of non-muscle tags
    if (activeType !== 'All') {
      const targetLower = activeType.toLowerCase();
      const muscleTag = tags[0];
      const otherTags = tags.slice(1).sort((a, b) => {
        if (a.label.toLowerCase() === targetLower) return -1;
        if (b.label.toLowerCase() === targetLower) return 1;
        return 0;
      });
      return [muscleTag, ...otherTags];
    }

    return tags;
  };

  const getFallbackImagePath = (ex: MovementData) => {
    const isFemale = isFemaleProfile(gender);
    const genderKey = isFemale ? 'female' : 'male';
    const baseName = (ex.en_display_name || '').split('(')[0].trim();
    return (
      exerciseImagesMap[ex.en_display_name]?.[genderKey] ||
      exerciseImagesMap[baseName]?.[genderKey] ||
      null
    );
  };

  const getImagePath = (ex: MovementData) => {
    const isFemale = isFemaleProfile(gender);
    const remoteUrl = isFemale
      ? ex.image_female_url || ex.image_male_url
      : ex.image_male_url || ex.image_female_url;

    if (remoteUrl) {
      return remoteUrl;
    }

    return getFallbackImagePath(ex);
  };

  const filteredList = useMemo(() => {
    return movements
      .filter((ex) => {
        if (ex.is_show_move === false) return false;

        // Search filter
        const name = ex.en_display_name || '';
        if (debouncedSearch && !name.toLowerCase().includes(debouncedSearch.toLowerCase()))
          return false;

        // Muscle Group filter (strictly based on tags with mapped fallback)
        if (activeMuscle !== 'All') {
          const target = activeMuscle.toLowerCase();
          const tagsLower = (ex.tags || []).map((t) => t.toLowerCase().trim());
          const hasMuscleTag =
            tagsLower.includes(target) ||
            (target === 'core' && tagsLower.some((t) => t.includes('core'))) ||
            ex.mapped_muscle_group?.toLowerCase() === target;

          if (!hasMuscleTag) return false;
        }

        // Exercise Type filter (based on move_types + cardio from tags)
        if (activeType !== 'All') {
          const targetType = activeType.toLowerCase();
          if (targetType === 'cardio') {
            const hasCardio =
              (ex.tags || []).some((t) => t.toLowerCase().trim() === 'cardio') ||
              (ex.move_types || []).some((t) => t.toLowerCase().trim() === 'cardio');
            if (!hasCardio) return false;
          } else {
            const moveTypes = (ex.move_types || []).map((t) => t.toLowerCase());
            if (!moveTypes.includes(targetType)) return false;
          }
        }

        return true;
      })
      .sort((a, b) => (a.en_display_name || '').localeCompare(b.en_display_name || ''));
  }, [movements, debouncedSearch, activeMuscle, activeType]);

  const grouped = useMemo(() => {
    return filteredList.reduce((acc: Record<string, MovementData[]>, ex) => {
      const name = ex.en_display_name || 'U';
      const letter = name.charAt(0).toUpperCase();
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(ex);
      return acc;
    }, {});
  }, [filteredList]);

  return (
    <aside className={`pg-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h3>Exercise Library</h3>
        <button className="sidebar-header-action" onClick={onClose}>
          {programGeneratorSVG.sidebar.exerciseLibraryClose}
        </button>
      </div>

      <div className="sidebar-content">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search movements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-search-btn" onClick={() => setSearch('')}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="filter">
          <div className="filter-group">
            <label>Muscle Groups</label>
            <div className="pills">
              {muscleGroups.map((m) => (
                <span
                  key={m}
                  className={`pill ${activeMuscle === m ? 'active' : ''}`}
                  onClick={() => setActiveMuscle(m)}
                >
                  {m}
                </span>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Exercise Type</label>
            <div className="pills">
              {exerciseTypes.map((t) => (
                <span
                  key={t}
                  className={`pill ${activeType === t ? 'active' : ''}`}
                  onClick={() => setActiveType(t)}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        <Droppable droppableId="library" isDropDisabled={true}>
          {(provided) => (
            <div className="exercise-list" ref={provided.innerRef} {...provided.droppableProps}>
              {isLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', opacity: 0.5 }}>
                  Loading exercises...
                </div>
              ) : (
                <>
                  <div className="alpha-group">
                    <DraggableExerciseItem
                      key="rest-library-item"
                      index={0}
                      ex={
                        {
                          id: 'rest-library-item',
                          en_display_name: 'Rest',
                          isRest: true,
                          image_male_url: '',
                          image_female_url: '',
                        } as any
                      }
                      tags={[{ label: 'Rest', type: 'yellow' }]}
                      getImagePath={() => null}
                    />
                  </div>
                  {(() => {
                    let globalIndex = 1;
                    return Object.keys(grouped).map((letter) => (
                      <div key={letter} className="alpha-group">
                        <div className="alpha-label">{letter}</div>
                        {grouped[letter].map((ex) => {
                          const tags = getDummyTags(ex);
                          const currIndex = globalIndex++;
                          return (
                            <DraggableExerciseItem
                              key={ex.id}
                              index={currIndex}
                              ex={ex}
                              tags={tags}
                              getImagePath={getImagePath}
                              getFallbackImagePath={getFallbackImagePath}
                            />
                          );
                        })}
                      </div>
                    ));
                  })()}
                  {provided.placeholder}
                </>
              )}
            </div>
          )}
        </Droppable>
      </div>

      <div className="sidebar-footer">
        <div className="footer-text">
          <p className="drag"> Drag any exercise into a day slot</p>
          <p className="movements">{filteredList.length} movements available</p>
        </div>
      </div>
    </aside>
  );
};
